import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import * as Sentry from '@sentry/react';
import cn from 'classnames';
import { $getRoot, EditorState } from 'lexical';
import {
  FC,
  KeyboardEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { EditablePlugin } from './plugins/EditablePlugin';
import { FocusCallbackPlugin } from './plugins/FocusCallbackPlugin';
import { InitialValuePlugin } from './plugins/InitialValuePlugin';
import { InsertSlotPlugin } from './plugins/InsertSlotPlugin';
import {
  SelectionTextPlugin,
  SlotSelectionSnapshot,
} from './plugins/SelectionTextPlugin';
import { SlotNavigationPlugin } from './plugins/SlotNavigationPlugin';
import { SlotContext, SlotContextValue } from './SlotContext';
import styles from './SlotEditor.module.scss';
import { SlotNode } from './SlotNode';

type SlotEditorProps = {
  className?: string;
  value: string;
  slots: Record<string, string[]> | null | undefined;
  slotInstructions?: Record<string, string> | null;
  placeholder?: string;
  readOnly?: boolean;
  autoFocus?: boolean;
  insertSlotRef?: React.MutableRefObject<((slotName: string) => void) | null>;
  selectionTextRef?: React.MutableRefObject<SlotSelectionSnapshot | null>;
  onChange: (text: string) => void;
  onSlotClick: (slotName: string) => void;
  onSlotCreate: (slotName: string, firstValue: string) => void;
  onEditorFocus?: () => void;
  onEditorBlur?: () => void;
  onEnter?: () => void;
  onTab?: (isShiftPressed: boolean) => void;
};

export const SlotEditor: FC<SlotEditorProps> = (props) => {
  const {
    className,
    value,
    slots,
    slotInstructions,
    placeholder,
    readOnly,
    autoFocus,
    insertSlotRef,
    selectionTextRef,
    onChange,
    onSlotClick,
    onSlotCreate,
    onEditorFocus,
    onEditorBlur,
    onEnter,
    onTab,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const initialConfig = useMemo(
    () => ({
      namespace: 'SlotEditor',
      theme: {
        root: styles.editorRoot,
        paragraph: styles.editorParagraph,
      },
      nodes: [SlotNode],
      editable: !readOnly,
      onError: (error: Error) => {
        Sentry.captureException(error);
      },
    }),
    [readOnly],
  );

  const handleChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        const root = $getRoot();
        const text = root.getTextContent();
        onChange(text);
      });
    },
    [onChange],
  );

  const slotContextValue = useMemo<SlotContextValue>(
    () => ({
      slots,
      slotInstructions,
      onSlotClick,
      onSlotCreate,
      readOnly,
    }),
    [slots, slotInstructions, onSlotClick, onSlotCreate, readOnly],
  );

  const showGhostBorder = isHovered && !isFocused && !readOnly;
  const showFullBorder = isFocused && !readOnly;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (readOnly) {
      return;
    }

    if (event.key === 'Enter' && onEnter) {
      event.preventDefault();
      onEnter();
      return;
    }

    if (event.key === 'Tab' && onTab) {
      event.preventDefault();
      onTab(event.shiftKey);
    }
  };

  return (
    <SlotContext.Provider value={slotContextValue}>
      <LexicalComposer initialConfig={initialConfig}>
        <div
          ref={containerRef}
          className={cn(
            styles.container,
            showFullBorder && styles.focused,
            showGhostBorder && styles.hovered,
            readOnly && styles.readOnly,
            className,
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onFocus={() => {
            setIsFocused(true);
            onEditorFocus?.();
          }}
          onBlur={() => {
            setIsFocused(false);
            onEditorBlur?.();
          }}
          onPointerDownCapture={() => {
            onEditorFocus?.();
          }}
        >
          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                className={cn(
                  styles.contentEditable,
                  readOnly && styles.contentEditableReadOnly,
                )}
                onKeyDown={handleKeyDown}
              />
            }
            placeholder={
              placeholder ? (
                <span className={styles.placeholder}>{placeholder}</span>
              ) : null
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <OnChangePlugin onChange={handleChange} />
          <InsertSlotPlugin />
          <InitialValuePlugin value={value} />
          <SelectionTextPlugin selectionTextRef={selectionTextRef} />
          <SlotNavigationPlugin />
          <FocusCallbackPlugin
            onFocus={onEditorFocus}
            onBlur={onEditorBlur}
            insertSlotRef={insertSlotRef}
          />
          <EditablePlugin readOnly={readOnly ?? false} />
          {autoFocus && <AutoFocusPlugin />}
        </div>
      </LexicalComposer>
    </SlotContext.Provider>
  );
};
